import { useMemo } from 'react';
import type { NetworkVenue } from '../api/client';

// Static, stylised GCC map from the design (MC-Map). No tile library, no API key, no
// network dependency during a demo. Production path: react-leaflet with vector tiles,
// positioning venues by their real lat/lng instead of city anchors.
const LAND =
  'M0 0H1000V700H0Z M187 32L387 129L520 175L700 235L753 242L820 290L853 343L1000 380L1000 520L906 471L753 375L760 293L733 331L685 369L625 417L560 440L470 430L440 407L435 363L427 305L400 330L387 407L360 400L340 293L300 240L230 150L199 104Z';
const COAST =
  'M187 32L387 129L520 175L700 235L753 242L820 290L853 343L1000 380M1000 520L906 471L753 375L760 293L733 331L685 369L625 417L560 440L470 430L440 407L435 363L427 305L400 330L387 407L360 400L340 293L300 240L230 150L199 104L187 32';

/** City anchors in map space (from the design), with the real-world centre they stand for. */
export const CITIES: Record<string, { x: number; y: number; lat: number; lng: number; label: [number, number] }> = {
  Riyadh: { x: 112, y: 400, lat: 24.7136, lng: 46.6753, label: [120, 440] },
  'Kuwait City': { x: 199, y: 104, lat: 29.3759, lng: 47.9774, label: [220, 110] },
  Manama: { x: 372, y: 290, lat: 26.2285, lng: 50.586, label: [330, 270] },
  Doha: { x: 435, y: 363, lat: 25.2854, lng: 51.531, label: [446, 404] },
  'Abu Dhabi': { x: 625, y: 417, lat: 24.4539, lng: 54.3773, label: [596, 458] },
  Dubai: { x: 685, y: 369, lat: 25.2048, lng: 55.2708, label: [704, 362] },
  Muscat: { x: 906, y: 471, lat: 23.588, lng: 58.3829, label: [914, 510] },
};

export type Worst = 'healthy' | 'degraded' | 'offline';
export function worstOf(venues: NetworkVenue[]): Worst {
  if (venues.some((v) => v.hasOfflineScreen || v.health === 'offline')) return 'offline';
  if (venues.some((v) => v.health === 'degraded')) return 'degraded';
  return 'healthy';
}

const hex = (r: number) => `M0 ${-r}L${r * 0.866} ${-r / 2}V${r / 2}L0 ${r}L${-r * 0.866} ${r / 2}V${-r / 2}Z`;
const statusVar: Record<Worst, string> = { healthy: 'var(--status-healthy)', degraded: 'var(--status-degraded)', offline: 'var(--status-offline)' };
const KM = 170; // map units per degree when zoomed into a city

interface Props {
  venues: NetworkVenue[];
  dimmed: (v: NetworkVenue) => boolean;
  zoomCity: string | null;
  selectedId: string | null;
  onZoom(city: string | null): void;
  onSelect(venue: NetworkVenue): void;
}

export function NetworkMap({ venues, dimmed, zoomCity, selectedId, onZoom, onSelect }: Props) {
  const byCity = useMemo(() => {
    const m = new Map<string, NetworkVenue[]>();
    for (const v of venues) if (CITIES[v.city]) (m.get(v.city) ?? m.set(v.city, []).get(v.city)!).push(v);
    return m;
  }, [venues]);

  const zoom = zoomCity ? CITIES[zoomCity] : null;
  const viewBox = zoom ? `${zoom.x - 150} ${zoom.y - 105} 300 210` : '0 0 1000 700';
  const pos = (v: NetworkVenue) => {
    const c = CITIES[v.city]!;
    if (v.lat != null && v.lng != null) return { x: c.x + (v.lng - c.lng) * KM, y: c.y - (v.lat - c.lat) * KM };
    // No coordinates: at the city's centre, spread round a small ring if there are several.
    const unplaced = (byCity.get(v.city) ?? []).filter((x) => x.lat == null || x.lng == null);
    const i = unplaced.indexOf(v);
    if (unplaced.length < 2) return { x: c.x, y: c.y };
    const a = (2 * Math.PI * i) / unplaced.length;
    return { x: c.x + Math.cos(a) * 14, y: c.y + Math.sin(a) * 14 };
  };

  return (
    <svg className="mc-map__svg" viewBox={viewBox} preserveAspectRatio="xMidYMid slice" role="img" aria-label="Stylised map of the Gulf with venue clusters">
      <defs>
        <pattern id="mc-dots" width="40" height="40" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="0.9" fill="var(--bm-white)" fillOpacity="0.12" />
        </pattern>
        <pattern id="mc-hx" width="96" height="55.43" patternUnits="userSpaceOnUse">
          <path d="M64 27.71L48 55.43L16 55.43L0 27.71L16 0L48 0ZM64 27.71L96 27.71" fill="none" stroke="var(--bm-white)" strokeOpacity="0.07" strokeWidth="0.8" />
        </pattern>
      </defs>
      <rect x="-500" y="-500" width="2000" height="1700" fill="var(--bm-map-sea)" />
      <rect x="-500" y="-500" width="2000" height="1700" fill="url(#mc-dots)" />
      <path d={LAND} fill="var(--bm-map-land)" fillRule="evenodd" />
      <path d={LAND} fill="url(#mc-hx)" fillRule="evenodd" />
      <path d={COAST} fill="none" stroke="var(--bm-blue-arctic)" strokeOpacity="0.45" strokeWidth="1.2" />
      <ellipse cx="372" cy="306" rx="6" ry="11" fill="var(--bm-map-land)" stroke="var(--bm-blue-arctic)" strokeOpacity="0.45" strokeWidth="1.2" />
      <g fontFamily="Inter, system-ui, sans-serif" fontSize={zoom ? 5 : 12} fill="var(--bm-blue-arctic)">
        {Object.entries(CITIES).map(([name, c]) => (
          <text key={name} x={c.label[0]} y={c.label[1]}>
            {name}
          </text>
        ))}
        {!zoom && (
          <text x="560" y="300" fontSize="13" fillOpacity="0.6" letterSpacing="3">
            ARABIAN GULF
          </text>
        )}
      </g>

      {!zoom &&
        [...byCity].map(([city, list]) => {
          const c = CITIES[city]!;
          const r = Math.min(24, 14 + Math.sqrt(list.length) * 2);
          const worst = worstOf(list);
          const hasReal = list.some((v) => v.source !== 'simulated');
          return (
            <g
              key={city}
              transform={`translate(${c.x} ${c.y})`}
              className="mc-map__cluster"
              role="button"
              tabIndex={0}
              aria-label={`${city}: ${list.length} venues, ${worst}. Zoom in`}
              onClick={() => onZoom(city)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onZoom(city)}
            >
              <path d={hex(r)} fill="var(--bm-white)" stroke={statusVar[worst]} strokeWidth="3" />
              <text y="4" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="500" fill="var(--bm-blue-deep)">
                {list.length}
              </text>
              {hasReal && <circle r={r + 6} fill="none" stroke="var(--accent)" strokeOpacity="0.8" strokeWidth="2" className="mc-map__pulse" />}
            </g>
          );
        })}

      {zoom &&
        (byCity.get(zoomCity!) ?? []).map((v) => {
          const p = pos(v);
          const real = v.source !== 'simulated';
          const worst: Worst = v.hasOfflineScreen || v.health === 'offline' ? 'offline' : v.health === 'degraded' ? 'degraded' : 'healthy';
          const selected = v.id === selectedId;
          return (
            <g
              key={v.id}
              transform={`translate(${p.x} ${p.y})`}
              className="mc-map__venue"
              opacity={dimmed(v) ? 0.25 : 1}
              role="button"
              tabIndex={0}
              aria-label={`${v.name}${real ? ' (real, live devices)' : ' (simulated)'}: ${v.health}`}
              onClick={() => onSelect(v)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(v)}
            >
              {real ? (
                <>
                  <circle r="9" fill="var(--accent)" fillOpacity="0.18" stroke="var(--bm-blue-arctic)" strokeOpacity="0.6" strokeDasharray="1.5 1.5" strokeWidth="0.5" />
                  <path d={hex(5)} fill="var(--bm-white)" stroke={statusVar[worst]} strokeWidth="1.5" />
                  <circle r="7" fill="none" stroke="var(--accent)" strokeWidth="1" className="mc-map__pulse" />
                </>
              ) : (
                // Simulated estate: smaller, flat, dashed edge, so it never reads as a live device.
                <path d={hex(3.2)} fill={statusVar[worst]} fillOpacity="0.75" stroke="var(--bm-white)" strokeOpacity="0.5" strokeDasharray="0.8 0.8" strokeWidth="0.4" />
              )}
              {selected && <path d={hex(real ? 8 : 5.5)} fill="none" stroke="var(--bm-white)" strokeWidth="0.8" />}
              {(real || selected) && (
                <g transform="translate(9 -6)" fontFamily="Inter, system-ui, sans-serif">
                  <rect width={Math.max(52, v.name.length * 2.6 + 10)} height="13" rx="2" fill="var(--bm-white)" />
                  <text x="3" y="5.5" fontSize="3.6" fontWeight="500" fill="var(--bm-ink)">
                    {v.name}
                  </text>
                  <text x="3" y="10" fontSize="3" fill={real ? 'var(--accent)' : 'var(--simulated-ink)'}>
                    {real ? `Real · ${v.screensOnline} of ${v.screensTotal} screens online` : 'Simulated venue'}
                  </text>
                </g>
              )}
            </g>
          );
        })}
    </svg>
  );
}
